import {Injectable} from '@angular/core';
import {
  collection,
  deleteDoc,
  doc,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  QueryDocumentSnapshot,
  setDoc,
} from '@angular/fire/firestore';
import {IStore} from '../../shared-models/store/store';
import {from, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {IDebtorDetails} from '../../../features/quotes/models/debtors';
import {createSharedPaths} from '../database-paths.shared';
import {CoreService} from 'src/app/features/core/service';

type IDebtorRecord = IDebtorDetails & {
  debtorId?: string;
  isDebtorSelected?: boolean;
  account?: string;
};

@Injectable({
  providedIn: 'root',
})
export class CollectionSharedDebtorsService extends CoreService {

  constructor(protected override firestore: Firestore) {
    super(firestore);
  }

  async setDocument<DataType>(
    path: string,
    data: DataType,
  ): Promise<void> {
    try {
      const docRef = doc(this.firestore, path);
      await setDoc(docRef, data, {merge: true});
    } catch (error) {
      console.error(error);
    }
  }

  async getDocument<DataType>(
    path: string,
  ): Promise<DataType> {
    try {
      const docRef = doc(this.firestore, path);
      const docSnapshot = await getDoc(docRef);
      if (docSnapshot.exists()) {
        return docSnapshot.data() as DataType;
      } else {
        return {} as DataType;
      }
    } catch (error) {
      console.error(error);
    }
  }

  async deleteDocument<DataType>(
    path: string,
  ): Promise<void> {
    try {
      const docRef = doc(this.firestore, path);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting document at path: ${path}\n`, error);
    }
  }

  getDebtorsByStore(store: IStore): Observable<Record<string, IDebtorRecord>> {
    if (!store?.storeId) {
      return of({});
    }

    const collectionRef = collection(this.firestore, createSharedPaths().setStoreId(store.storeId).debtors());
    return from(getDocs(collectionRef)).pipe(
      map((snapshot) => this.mapDebtorSnapshots(snapshot.docs)),
      catchError(() => of({})),
    );
  }

  private mapDebtorSnapshots(docs: QueryDocumentSnapshot[]): Record<string, IDebtorRecord> {
    const debtors: Record<string, IDebtorRecord> = {};
    docs.forEach((docSnapshot: QueryDocumentSnapshot): void => {
      const debtorData = docSnapshot.data();
      const debtorId = this.resolveDebtorId(debtorData, docSnapshot.id);
      if (!debtorId) {
        return;
      }
      debtors[debtorId] = debtorData as IDebtorRecord;
      debtors[debtorId].debtorId = debtorId;
      debtors[debtorId].account = debtors[debtorId].account ?? debtorId;
      debtors[debtorId].isDebtorSelected = false;
    });
    return debtors;
  }

  private resolveDebtorId(data: DocumentData, documentId: string): string {
    const account = data['account'] as string | undefined;
    const debtorId = data['debtorId'] as string | undefined;
    const id = data['id'] as string | undefined;
    if (account?.trim()) {
      return account.trim();
    }
    if (debtorId?.trim()) {
      return debtorId.trim();
    }
    if (id?.trim()) {
      return id.trim();
    }
    if (documentId?.trim()) {
      return documentId.trim();
    }
    return '';
  }
}
